import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CoreOrganization } from '@/lib/zifekCore';
import {
  criteriaFor,
  fetchVerificationStats,
  LEVEL_CRITERIA,
  listVerifications,
  requestVerification,
  VERIFICATION_DISCLAIMER,
  VERIFICATION_LEVELS,
  verificationLevelMeta,
  verificationStatusMeta,
  type CoreVerification,
  type VerificationCriterion,
  type VerificationLevel,
} from '@/lib/coreVerification';
import { getBusinessProfile } from '@/lib/coreBusinessProfile';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

export default function VerificationTab({ org }: Props) {
  const [verifications, setVerifications] = useState<CoreVerification[]>([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0, byLevel: {} as Record<string, number> });
  const [currentLevel, setCurrentLevel] = useState<string>('unverified');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [target, setTarget] = useState<VerificationLevel>('basic');
  const [checks, setChecks] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);
  const [doneMsg, setDoneMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [v, s, profile] = await Promise.all([
        listVerifications(org.id),
        fetchVerificationStats(org.id),
        getBusinessProfile(org.id),
      ]);
      setVerifications(v);
      setStats(s);
      setCurrentLevel(profile?.verification_level || 'unverified');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  const criteria: VerificationCriterion[] = useMemo(() => criteriaFor(target, Object.keys(checks).filter((k) => checks[k])), [target, checks]);

  const submit = async () => {
    setSubmitting(true);
    setError('');
    setDoneMsg('');
    try {
      await requestVerification({
        organizationId: org.id,
        tenantId: org.tenant_id,
        entityType: 'business',
        entityId: org.id,
        level: target,
        criteria,
      });
      setDoneMsg(`Demande « ${verificationLevelMeta(target).label} » envoyée. Elle sera examinée selon les critères définis par Zifek.`);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Envoi impossible.');
    } finally {
      setSubmitting(false);
    }
  };

  const levelMeta = verificationLevelMeta(currentLevel);
  const lastRequest = verifications[0];

  return (
    <div className="space-y-5">
      <p className="text-xs text-foreground-500 max-w-3xl">
        <span className="font-semibold text-foreground-700">Vérification</span> : plusieurs niveaux, du moins au plus engagé —
        <span className="font-medium text-foreground-600"> Unverified · Basic Verified · Business Verified · Professional Verified</span>.
      </p>

      <div className="p-3 bg-accent-50 border border-accent-200 rounded-md text-[11px] text-accent-900 flex items-start gap-2">
        <i className="ri-information-line mt-0.5"></i>
        <span>{VERIFICATION_DISCLAIMER}</span>
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4 sm:col-span-1">
          <p className="text-xs text-foreground-500">Niveau actuel</p>
          <span className={`inline-flex items-center gap-1.5 mt-1.5 px-3 py-1 rounded-full text-xs font-medium ${levelMeta.className}`}>
            <i className={levelMeta.icon}></i>{levelMeta.label}
          </span>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <p className="text-xs text-foreground-500">Demandes en cours</p>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.pending}</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <p className="text-xs text-foreground-500">Validées</p>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.approved}</p>
        </div>
      </div>

      {/* Les 4 niveaux */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {VERIFICATION_LEVELS.map((l) => {
          const active = currentLevel === l.key;
          return (
            <div key={l.key} className={`rounded-lg border p-4 ${active ? 'border-primary-300 bg-primary-50/50' : 'border-background-200/70 bg-background-50'}`}>
              <div className="flex items-center gap-2">
                <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${l.className}`}><i className={l.icon}></i></span>
                <p className="text-sm font-semibold text-foreground-900">{l.label}</p>
                {active && <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-primary-100 text-primary-900">actuel</span>}
              </div>
              <p className="text-[11px] text-foreground-500 mt-2">{l.desc}</p>
              {LEVEL_CRITERIA[l.key].length > 0 && (
                <ul className="mt-2 space-y-1">
                  {LEVEL_CRITERIA[l.key].map((c) => (
                    <li key={c.key} className="text-[11px] text-foreground-500 flex items-center gap-1.5"><i className="ri-checkbox-blank-circle-line text-[8px]"></i>{c.label}</li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      {/* Demande */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center"><i className="ri-shield-check-line"></i></span>
          <h4 className="text-sm font-semibold text-foreground-900">Demander une vérification</h4>
        </div>
        <div className="p-4 space-y-3">
          <div className="flex flex-wrap gap-2">
            {VERIFICATION_LEVELS.filter((l) => l.key !== 'unverified').map((l) => (
              <button key={l.key} onClick={() => { setTarget(l.key); setChecks({}); }} className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${target === l.key ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-700 hover:bg-background-200/70'}`}>
                {l.label}
              </button>
            ))}
          </div>

          <div className="space-y-2">
            {criteriaFor(target).map((c) => (
              <label key={c.key} className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!checks[c.key]} onChange={(e) => setChecks({ ...checks, [c.key]: e.target.checked })} className="w-4 h-4 accent-primary-500 cursor-pointer" />
                <span className="text-sm text-foreground-700">{c.label}</span>
              </label>
            ))}
          </div>

          <button onClick={submit} disabled={submitting} className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
            {submitting ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-send-plane-line"></i>} Envoyer la demande
          </button>
          {doneMsg && <p className="text-xs text-primary-700 flex items-center gap-1.5"><i className="ri-checkbox-circle-line"></i>{doneMsg}</p>}
        </div>
      </div>

      {/* Historique */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center"><i className="ri-history-line"></i></span>
          <h4 className="text-sm font-semibold text-foreground-900">Historique des vérifications</h4>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
        ) : verifications.length === 0 ? (
          <p className="px-4 py-6 text-xs text-foreground-400 text-center">Aucune demande de vérification pour le moment.</p>
        ) : (
          <div className="divide-y divide-background-200/60">
            {verifications.map((v) => {
              const lvl = verificationLevelMeta(v.level);
              const st = verificationStatusMeta(v.status);
              return (
                <div key={v.id} className="flex items-center gap-3 px-4 py-2.5">
                  <span className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${lvl.className}`}><i className={lvl.icon}></i></span>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-foreground-800 truncate">{lvl.label}</p>
                    <p className="text-[11px] text-foreground-400 truncate">
                      {v.requested_at ? new Date(v.requested_at).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                      {v.notes ? ` · ${v.notes}` : ''}
                    </p>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${st.className}`}>{st.label}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}